import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { EmployeeService } from '@core/services';
import { environment } from '@environments/environment';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Employee, EmployeeCreate, EmployeeUpdate } from '@core/types/employee.type';

@Injectable({ providedIn: 'root' })
export class EmployeeQuery {
  private readonly service = inject(EmployeeService);
  private readonly queryClient = inject(QueryClient);

  useEmployeesGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.employees.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<Employee>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateEmployee() {
    return injectMutation(() => ({
      mutationFn: (data: EmployeeCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees.all }),
    }));
  }

  useUpdateEmployee() {
    return injectMutation(() => ({
      mutationFn: (data: EmployeeUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees.all });
      },
    }));
  }

  useDeleteEmployee() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees.all }),
    }));
  }
}

